import React, { useEffect } from 'react'
import DOMPurify from 'dompurify' // still recommended
import { marked, Tokens } from 'marked'

import { cn } from '@/lib/utils'

/* ---------- 1.  Renderer overrides that use the new token signatures ---------- */
marked.use({
  renderer: {
    /* # Headings --------------------------------------------------------------- */
    heading({ tokens, depth }: Tokens.Heading) {
      const html = this.parser.parseInline(tokens) // inline children → HTML
      if (depth === 1)
        return `<h1 class="${cn('mb-2 text-2xl font-bold')}">${html}</h1>\n`
      if (depth === 2)
        return `<h2 class="${cn('mb-2 text-xl font-bold')}">${html}</h2>\n`
      return `<h${depth}>${html}</h${depth}>\n`
    },

    /* Paragraphs --------------------------------------------------------------- */
    paragraph({ tokens }: Tokens.Paragraph) {
      return `<p class="${cn('mb-4 text-sm')}">${this.parser.parseInline(tokens)}</p>\n`
    },

    /* Lists and list-items ----------------------------------------------------- */
    list(token: Tokens.List) {
      const tag = token.ordered ? 'ol' : 'ul'
      const cls = token.ordered ? '' : cn('list-disc')
      const body = token.items.map((item) => this.listitem(item)).join('')
      const start =
        token.ordered && token.start !== 1 ? ` start="${token.start}"` : ''
      return `<${tag}${start} class="${cls}">\n${body}</${tag}>\n`
    },
    listitem(token: Tokens.ListItem) {
      /* token.loose tells us if it’s a loose list; we keep marked’s own parser for children */
      return `<li class="${cn('ml-4')}">${this.parser.parse(token.tokens, !!token.loose)}</li>\n`
    },

    /* Tables ------------------------------------------------------------------- */
    table(token: Tokens.Table) {
      /* get marked’s default <table> and simply wrap it for horizontal scroll */
      const inner = this.constructor.prototype.table.call(this, token)
      return `<div class="overflow-x-auto">${inner}</div>\n`
    },
    tablecell(token: Tokens.TableCell) {
      const html = this.parser.parseInline(token.tokens)
      const type = token.header ? 'th' : 'td'
      const cls = token.header ? cn('text-nowrap pr-2') : cn('pr-4')
      const align = token.align ? ` align="${token.align}"` : ''
      return `<${type}${align} class="${cls}">${html}</${type}>\n`
    },
  },
})

/* ---------- 2.  React wrapper component -------------------------------------- */
export interface MarkdownRendererProps {
  children: string
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  children,
}) => {
  const [html, setHtml] = React.useState('')

  useEffect(() => {
    marked.parse(children, { async: true }).then((raw) => {
      setHtml(DOMPurify.sanitize(raw))
    })
  }, [children])

  return (
    <div
      style={{ fontSize: '0.8rem' }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
